import { useEffect, useState } from 'react';
import { COUNTDOWN } from '../constants/config';

export interface CountdownState {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  /** Час X позади, и `COUNT_UP_AFTER` включён — цифры идут вверх (сколько уже работает сервер). */
  countingUp: boolean;
  /** Час X позади, а счёт вверх выключен — показывать нечего, обе точки вывода прячут блок. */
  finished: boolean;
}

const compute = (target: number, countUpAfter: boolean): CountdownState => {
  const distance = target - Date.now();
  const elapsed = Math.abs(distance);

  return {
    days: Math.floor(elapsed / (1000 * 60 * 60 * 24)),
    hours: Math.floor((elapsed % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((elapsed % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((elapsed % (1000 * 60)) / 1000),
    countingUp: distance <= 0 && countUpAfter,
    finished: distance <= 0 && !countUpAfter,
  };
};

/**
 * Единственный источник отсчёта до запуска сервера — тикает раз в секунду.
 *
 * Живёт хуком, а не копией в компоненте, потому что таймер рисуется в двух местах
 * (геро-блок главной и плавающий `LaunchWidget`), и разъехаться им нельзя: после часа X
 * оба должны одновременно переключиться на счёт вверх либо одновременно исчезнуть.
 * Настройки — `COUNTDOWN` в `constants/config`.
 */
export const useCountdown = (
  targetDate: string = COUNTDOWN.TARGET_DATE,
  countUpAfter: boolean = COUNTDOWN.COUNT_UP_AFTER,
): CountdownState => {
  const target = new Date(targetDate).getTime();
  const [state, setState] = useState(() => compute(target, countUpAfter));

  useEffect(() => {
    setState(compute(target, countUpAfter));
    const timer = setInterval(() => setState(compute(target, countUpAfter)), 1000);
    return () => clearInterval(timer);
  }, [target, countUpAfter]);

  return state;
};
